import FetchDocumentTemplates from '@Application/Documents/Templates/Queries/FetchDocumentTemplates';
import fetchDocumentTemplates from '@Application/Documents/Templates/fetchDocumentTemplates';
import FetchTemplateAssignments from '@Application/Documents/TemplateAssignments/Queries/FetchTemplateAssignments';
import type { TemplateAssignmentScope } from '@Application/Documents/TemplateAssignments/TemplateAssignmentScope';
import fetchTemplateAssignments from '@Application/Documents/TemplateAssignments/fetchTemplateAssignments';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import { theme } from '@Guidelines/Theme/theme';
import TemplateAssignmentCard from '@UI/Pages/DocumentTemplates/Shared/TemplateAssignmentCard';
import useAccountDocumentTypes from '@UI/Hooks/useAccountDocumentTypes';
import useCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useCurrentFacility';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import { css } from '@emotion/react';
import { MapPin } from 'lucide-react';
import * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Skeleton } from '@verclam/ui-kit';

const cardsGridStyle = css`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(min(20rem, 100%), 1fr));
	gap: 1.25rem;
`;

const CardsSkeleton: React.FC = () => (
	<div css={cardsGridStyle}>
		<Skeleton height="10rem" borderRadius={theme.radii.xlarge} animation="wave" />
		<Skeleton height="10rem" borderRadius={theme.radii.xlarge} animation="wave" />
		<Skeleton height="10rem" borderRadius={theme.radii.xlarge} animation="wave" />
		<Skeleton height="10rem" borderRadius={theme.radii.xlarge} animation="wave" />
		<Skeleton height="10rem" borderRadius={theme.radii.xlarge} animation="wave" />
	</div>
);

const LOAD_ERROR_TITLE = 'Impossible de charger les gabarits.';

/**
 * Acts on the current facility — the header selector's own choice, never a list to enter. The gabarit
 * catalogue is read from the current organization (`useCurrentOrganization`), the very same catalogue
 * the organization screen manages; only the affectations themselves are scoped to the facility. The
 * card that renders each type is the one shared with the organization screen
 * (`Shared/TemplateAssignmentCard`) — this screen never passes it `children`, the channel
 * reserved for the organization-only list of facility overrides.
 *
 * `isResolved` describes the facility resolution alone, never the organization's: it must always be
 * tested alongside `currentFacility === null`, never the second by itself (`useCurrentFacility`'s own
 * docblock) — otherwise "Aucune agence" would flash ahead of the real answer while the facility context
 * is still settling.
 */
const FacilityDocumentTemplatesContent: React.FC = () => {
	const dispatch = useDispatch();
	const { currentOrganization } = useCurrentOrganization();
	const organizationId = currentOrganization?.id ?? null;
	const { currentFacility, isResolved } = useCurrentFacility();
	const facilityId = currentFacility?.id ?? null;

	const {
		documentTypes,
		error: documentTypesError,
		refetch: refetchDocumentTypes,
	} = useAccountDocumentTypes();

	const scope = React.useMemo<TemplateAssignmentScope | null>(
		() => (facilityId === null ? null : { type: 'facility', id: facilityId }),
		[facilityId],
	);

	const {
		data: templates,
		error: templatesError,
		refetch: refetchTemplates,
	} = useStoreQuery({
		selector: (state) => state.documentTemplatesStore.templates,
		stopOn: (value) => value !== null && value.organizationId === organizationId,
		query: () => {
			if (organizationId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchDocumentTemplates(new FetchDocumentTemplates(organizationId)));
		},
		deps: [organizationId],
	});

	const {
		data: assignments,
		error: assignmentsError,
		refetch: refetchAssignments,
	} = useStoreQuery({
		selector: (state) => state.documentTemplatesStore.facilityAssignments,
		stopOn: (value) => value !== null && value.scopeType === 'facility' && value.scopeId === facilityId,
		query: () => {
			if (scope === null) {
				return Promise.resolve();
			}

			return dispatch(fetchTemplateAssignments(new FetchTemplateAssignments(scope)));
		},
		deps: [facilityId],
	});

	const handleRetry = React.useCallback(() => {
		refetchTemplates();
		refetchAssignments();
		refetchDocumentTypes();
	}, [refetchTemplates, refetchAssignments, refetchDocumentTypes]);

	if (!isResolved) {
		return <CardsSkeleton />;
	}

	if (currentFacility === null) {
		return <EmptyState title="Aucune agence n’est sélectionnée." icon={<MapPin size={28} />} />;
	}

	if (templates === null && templatesError === null) {
		return <CardsSkeleton />;
	}

	if (templates === null) {
		return <ErrorState title={LOAD_ERROR_TITLE} onRetry={handleRetry} />;
	}

	if (assignments === null && assignmentsError === null) {
		return <CardsSkeleton />;
	}

	if (assignments === null) {
		return <ErrorState title={LOAD_ERROR_TITLE} onRetry={handleRetry} />;
	}

	if (documentTypes === null && documentTypesError === null) {
		return <CardsSkeleton />;
	}

	if (documentTypes === null) {
		return <ErrorState title={LOAD_ERROR_TITLE} onRetry={handleRetry} />;
	}

	return (
		<div css={cardsGridStyle}>
			{assignments.list.map((assignment) => (
				<TemplateAssignmentCard
					key={assignment.documentTypeId}
					assignment={assignment}
					scope={{ type: 'facility', id: currentFacility.id }}
					templates={templates}
					documentTypes={documentTypes}
				/>
			))}
		</div>
	);
};

export default FacilityDocumentTemplatesContent;
